import React, { useState } from "react";

/*
  Hawkam Logo Preview
  8 different luxury options for the Arabic "ح" mark
  All designs use the brand palette:
   - Gold:  #D4AF37 (primary)
   - Gold soft: #E8C86A
   - Deep navy: #0A0F1C
   - Ivory: #F6F1E4
*/

const GOLD = "#D4AF37";
const GOLD_SOFT = "#E8C86A";
const NAVY = "#0A0F1C";
const IVORY = "#F6F1E4";

// ---------- Option 1: Kufic Square (modern) ----------
const Logo1 = ({ size = 140 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="200" height="200" rx="18" fill={NAVY} />
    <rect x="18" y="18" width="164" height="164" rx="10" fill="none" stroke={GOLD} strokeWidth="2.5" />
    <rect x="28" y="28" width="144" height="144" rx="4" fill="none" stroke={GOLD} strokeWidth="0.8" strokeDasharray="2 4" opacity="0.6" />
    <text
      x="100"
      y="138"
      textAnchor="middle"
      fontFamily="'Amiri', 'Noto Naskh Arabic', serif"
      fontSize="140"
      fontWeight="700"
      fill={GOLD}
    >ح</text>
  </svg>
);

// ---------- Option 2: Classical Circle with Ornament ----------
const Logo2 = ({ size = 140 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <circle cx="100" cy="100" r="98" fill={NAVY} />
    <circle cx="100" cy="100" r="92" fill="none" stroke={GOLD} strokeWidth="2.5" />
    <circle cx="100" cy="100" r="82" fill="none" stroke={GOLD} strokeWidth="0.6" opacity="0.7" />
    {/* Decorative dots at cardinal points */}
    {[0, 90, 180, 270].map((a) => (
      <circle
        key={a}
        cx={100 + 87 * Math.cos((a * Math.PI) / 180)}
        cy={100 + 87 * Math.sin((a * Math.PI) / 180)}
        r="2.4"
        fill={GOLD}
      />
    ))}
    <text
      x="100"
      y="140"
      textAnchor="middle"
      fontFamily="'Amiri', 'Scheherazade New', serif"
      fontSize="130"
      fontWeight="700"
      fill={GOLD}
    >ح</text>
  </svg>
);

// ---------- Option 3: Bilingual Monogram ح + H ----------
const Logo3 = ({ size = 140 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="200" height="200" rx="18" fill={IVORY} />
    <rect x="10" y="10" width="180" height="180" rx="12" fill="none" stroke={GOLD} strokeWidth="1.5" />
    <text
      x="72"
      y="145"
      textAnchor="middle"
      fontFamily="'Playfair Display', serif"
      fontSize="150"
      fontWeight="600"
      fill={NAVY}
      opacity="0.15"
    >H</text>
    <text
      x="118"
      y="145"
      textAnchor="middle"
      fontFamily="'Amiri', serif"
      fontSize="120"
      fontWeight="700"
      fill={GOLD}
    >ح</text>
  </svg>
);

// ---------- Option 4: Minimal with gold underline ----------
const Logo4 = ({ size = 140 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="200" height="200" fill={NAVY} />
    <text
      x="100"
      y="130"
      textAnchor="middle"
      fontFamily="'Amiri', serif"
      fontSize="150"
      fontWeight="600"
      fill={IVORY}
    >ح</text>
    <line x1="60" y1="160" x2="140" y2="160" stroke={GOLD} strokeWidth="2" />
    <circle cx="100" cy="160" r="3.5" fill={GOLD} />
  </svg>
);

// ---------- Option 5: Shield Badge (heraldic) ----------
const Logo5 = ({ size = 140 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="shieldGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#111827" />
        <stop offset="100%" stopColor={NAVY} />
      </linearGradient>
    </defs>
    <path
      d="M100 8 L182 32 L182 100 C182 145 145 178 100 192 C55 178 18 145 18 100 L18 32 Z"
      fill="url(#shieldGrad)"
      stroke={GOLD}
      strokeWidth="2.5"
    />
    <path
      d="M100 22 L170 42 L170 100 C170 138 138 165 100 178 C62 165 30 138 30 100 L30 42 Z"
      fill="none"
      stroke={GOLD}
      strokeWidth="0.8"
      opacity="0.6"
    />
    <text
      x="100"
      y="135"
      textAnchor="middle"
      fontFamily="'Amiri', serif"
      fontSize="115"
      fontWeight="700"
      fill={GOLD}
    >ح</text>
  </svg>
);

// ---------- Option 6: Hexagon (modern geometric) ----------
const Logo6 = ({ size = 140 }) => {
  const hex = (r) =>
    [0, 60, 120, 180, 240, 300]
      .map((a) => {
        const rad = ((a - 30) * Math.PI) / 180;
        return `${100 + r * Math.cos(rad)},${100 + r * Math.sin(rad)}`;
      })
      .join(" ");
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
      <polygon points={hex(96)} fill={NAVY} />
      <polygon points={hex(96)} fill="none" stroke={GOLD} strokeWidth="2.5" />
      <polygon points={hex(84)} fill="none" stroke={GOLD} strokeWidth="0.7" opacity="0.6" />
      <text
        x="100"
        y="140"
        textAnchor="middle"
        fontFamily="'Amiri', serif"
        fontSize="130"
        fontWeight="700"
        fill={GOLD}
      >ح</text>
    </svg>
  );
};

// ---------- Option 7: Crown Laurel (executive) ----------
const Logo7 = ({ size = 140 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="200" height="200" rx="18" fill={NAVY} />
    {/* Left laurel */}
    <path
      d="M 40 100 Q 30 80 40 60 Q 45 75 55 80"
      fill="none"
      stroke={GOLD}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M 40 110 Q 32 130 42 150 Q 48 135 58 130"
      fill="none"
      stroke={GOLD}
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* Right laurel */}
    <path
      d="M 160 100 Q 170 80 160 60 Q 155 75 145 80"
      fill="none"
      stroke={GOLD}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M 160 110 Q 168 130 158 150 Q 152 135 142 130"
      fill="none"
      stroke={GOLD}
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* Small star on top */}
    <polygon
      points="100,32 103,42 113,42 105,48 108,58 100,52 92,58 95,48 87,42 97,42"
      fill={GOLD}
    />
    <text
      x="100"
      y="140"
      textAnchor="middle"
      fontFamily="'Amiri', serif"
      fontSize="120"
      fontWeight="700"
      fill={GOLD}
    >ح</text>
  </svg>
);

// ---------- Option 8: Kufic block letter (fully custom path) ----------
const Logo8 = ({ size = 140 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="200" height="200" rx="6" fill={IVORY} />
    <rect x="8" y="8" width="184" height="184" rx="2" fill="none" stroke={GOLD} strokeWidth="1" />
    {/* Custom kufic-style ح made from rectangles */}
    <g fill={NAVY}>
      {/* Horizontal top bar */}
      <rect x="46" y="70" width="108" height="14" />
      {/* Right vertical stem */}
      <rect x="140" y="70" width="14" height="60" />
      {/* Bottom curl (block) */}
      <rect x="46" y="116" width="108" height="14" />
      {/* Left vertical stem */}
      <rect x="46" y="70" width="14" height="60" />
      {/* Inner dot (representing the letter's nuqta area) */}
      <rect x="92" y="94" width="16" height="12" fill={GOLD} />
    </g>
  </svg>
);

const options = [
  {
    id: 1,
    Comp: Logo1,
    name: "Kufic Square — كوفي مربّع",
    desc: "خط عربي داخل إطار مربّع ذهبي على خلفية داكنة — عصري وفاخر",
  },
  {
    id: 2,
    Comp: Logo2,
    name: "Classical Circle — دائرة كلاسيكية",
    desc: "دائرتان ذهبيتان مع نقاط زخرفية على الأطراف — كلاسيكي رسمي",
  },
  {
    id: 3,
    Comp: Logo3,
    name: "Bilingual Monogram — مونوغرام ثنائي اللغة",
    desc: "دمج الحرف العربي ح مع اللاتيني H على خلفية عاجية — بايلينغوال",
  },
  {
    id: 4,
    Comp: Logo4,
    name: "Minimal Underline — مينيمال",
    desc: "حرف ح بخط رفيع مع خط ذهبي تحته ونقطة مركزية — أنيق ومينيمال",
  },
  {
    id: 5,
    Comp: Logo5,
    name: "Heraldic Shield — درع الحوكمة",
    desc: "شكل درع فخم بحدود مزدوجة ذهبية — يوحي بالثقة والحماية",
  },
  {
    id: 6,
    Comp: Logo6,
    name: "Hexagon — سداسي هندسي",
    desc: "إطار سداسي هندسي حديث مع الحرف في الوسط — تكنوقراطي فاخر",
  },
  {
    id: 7,
    Comp: Logo7,
    name: "Laurel Executive — إكليل تنفيذي",
    desc: "فرعا غار ذهبيان مع نجمة صغيرة أعلى الحرف — تنفيذي/حكومي",
  },
  {
    id: 8,
    Comp: Logo8,
    name: "Block Kufic Custom — كوفي كتلي مخصص",
    desc: "حرف ح مبني من كتل هندسية على خلفية عاجية — قوي وفريد",
  },
];

const Wordmark = ({ dark = true }) => (
  <div className="flex flex-col items-center gap-1 mt-3">
    <div
      className="text-2xl font-bold tracking-wide"
      style={{ color: dark ? IVORY : NAVY, fontFamily: "'Amiri', serif" }}
    >
      حوكم
    </div>
    <div
      className="text-[10px] tracking-[0.35em] uppercase"
      style={{ color: GOLD, letterSpacing: "0.35em" }}
    >
      HAWKAM · CONSULTING
    </div>
  </div>
);

export default function LogoPreview() {
  const [selected, setSelected] = useState(null);

  return (
    <div
      dir="rtl"
      style={{ backgroundColor: "#f5f2ea", minHeight: "100vh" }}
      className="py-10 px-6"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div
            className="text-[10px] tracking-[0.4em] mb-3"
            style={{ color: GOLD, letterSpacing: "0.4em" }}
          >
            HAWKAM · BRAND IDENTITY
          </div>
          <h1
            className="text-4xl sm:text-5xl font-bold mb-3"
            style={{ color: NAVY, fontFamily: "'Amiri', serif" }}
          >
            اختر شعار حوكم الفاخر
          </h1>
          <p className="text-sm text-gray-600 max-w-xl mx-auto">
            فيما يلي 8 خيارات لشعار المنصة. تصفّح، انقر على الشعار الذي يعجبك،
            ثم أخبرني برقم الخيار لأطبّقه في جميع صفحات الموقع.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {options.map(({ id, Comp, name, desc }) => {
            const isSelected = selected === id;
            const isLight = id === 3 || id === 8; // ivory background variants
            return (
              <button
                key={id}
                type="button"
                data-testid={`logo-option-${id}`}
                onClick={() => setSelected(id)}
                className={`relative flex flex-col items-center rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl bg-white border-2 ${
                  isSelected ? "shadow-2xl" : "shadow-md"
                }`}
                style={{
                  borderColor: isSelected ? GOLD : "transparent",
                }}
              >
                <div
                  className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{
                    backgroundColor: NAVY,
                    color: GOLD,
                    border: `1.5px solid ${GOLD}`,
                  }}
                >
                  {id}
                </div>

                <div className="my-2">
                  <Comp size={140} />
                </div>

                <Wordmark dark={!isLight ? false : false} />

                <div className="mt-4 text-center">
                  <div
                    className="text-sm font-semibold"
                    style={{ color: NAVY }}
                  >
                    {name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{desc}</div>
                </div>

                {isSelected && (
                  <div
                    className="mt-4 text-xs px-3 py-1 rounded-full"
                    style={{
                      backgroundColor: GOLD,
                      color: NAVY,
                    }}
                  >
                    ✓ مُختار — أخبرني بالرقم لأطبّقه
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-12 rounded-xl p-6 text-center" style={{ backgroundColor: NAVY }}>
          <div className="text-xs tracking-[0.4em] mb-2" style={{ color: GOLD }}>
            HOW IT WILL LOOK · شكل التطبيق النهائي
          </div>
          <p className="text-sm mb-6" style={{ color: IVORY }}>
            بعد اختيارك، سيتم تطبيق الشعار على: Header، Footer، Favicon، صفحة
            تسجيل الدخول، ونوافذ التحميل.
          </p>
          {selected && (
            <div className="flex items-center justify-center gap-4 flex-wrap">
              {(() => {
                const Comp = options.find((o) => o.id === selected).Comp;
                return (
                  <>
                    <div className="flex items-center gap-3 bg-black/40 backdrop-blur px-4 py-3 rounded-lg">
                      <Comp size={48} />
                      <div className="text-right">
                        <div
                          style={{
                            color: IVORY,
                            fontFamily: "'Amiri', serif",
                          }}
                          className="text-lg font-bold leading-tight"
                        >
                          حوكم
                        </div>
                        <div
                          className="text-[9px] tracking-[0.3em]"
                          style={{ color: GOLD }}
                        >
                          HAWKAM
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg">
                      <Comp size={40} />
                      <div className="text-right">
                        <div
                          style={{ color: NAVY, fontFamily: "'Amiri', serif" }}
                          className="text-base font-bold leading-tight"
                        >
                          حوكم للاستشارات
                        </div>
                        <div
                          className="text-[9px] tracking-[0.3em]"
                          style={{ color: GOLD }}
                        >
                          HAWKAM CONSULTING
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
